import DoneIcon from '@mui/icons-material/CheckCircleOutlined';
import DrawIcon from '@mui/icons-material/DrawOutlined';
import HourglassIcon from '@mui/icons-material/HourglassEmptyOutlined';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';

import { cn } from '@/shared/lib/cn';

import type { ItemNeed } from './grouping';

/**
 * What an item is waiting on, as a mark.
 *
 * The list is scanned before it is read, so each card leads with the *verb* —
 * a pen for a signature, a note for a payment. The mark never carries the
 * meaning alone: the card states the need in words beside it (§11). It exists
 * to make a column of cards sortable by eye, not to replace the sentence.
 */

const NEED_ICONS: Record<ItemNeed, typeof DrawIcon> = {
  sign: DrawIcon,
  pay: PaymentsIcon,
  signAndPay: DrawIcon,
  waiting: HourglassIcon,
};

/** Money is the state with a late fee behind it, so it takes the strong tone.
 *  A signature is a task, not a debt — it reads amber. `waiting` takes the same
 *  blue as a `progress` StatusTag: it is not this director's task right now, and
 *  the grey it used to carry made it read as "nothing is happening".
 *
 *  Drawn from the same `statusTag` tokens as the chip, because the badge and the
 *  chip sit on one row and must never disagree about a state. */
const NEED_CLASSES: Record<ItemNeed, string> = {
  pay: 'bg-tag-danger text-tag-danger-ink inset-ring-tag-danger-edge',
  signAndPay: 'bg-tag-danger text-tag-danger-ink inset-ring-tag-danger-edge',
  sign: 'bg-tag-warning text-tag-warning-ink inset-ring-tag-warning-edge',
  waiting: 'bg-tag-progress text-tag-progress-ink inset-ring-tag-progress-edge',
};

interface NeedBadgeProps {
  need: ItemNeed;
  /** Set on a step the flow has finished, where the mark reports an outcome
   *  rather than a task. */
  done?: boolean;
}

export const NeedBadge = ({ need, done = false }: NeedBadgeProps) => {
  const Icon = done ? DoneIcon : NEED_ICONS[need];

  return (
    <span
      aria-hidden
      className={cn(
        'flex size-9 shrink-0 items-center justify-center rounded-xl inset-ring [&>svg]:text-[18px]',
        done
          ? 'bg-tag-success text-tag-success-ink inset-ring-tag-success-edge'
          : NEED_CLASSES[need],
      )}
    >
      <Icon fontSize="inherit" />
    </span>
  );
};
